import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import type { FilterDescriptor } from "@/lib/erp";

type TranslationModule = Record<string, string>;

interface ActiveFiltersDisplayProps {
  filters: FilterDescriptor[];
  onRemoveFilter: (index: number) => void;
  onClearAll: () => void;
  translations?: TranslationModule;
}

export function ActiveFiltersDisplay({
  filters,
  onRemoveFilter,
  onClearAll,
  translations,
}: ActiveFiltersDisplayProps) {
  const t = translations || {};

  // Group filters by section
  const groupedFilters = useMemo(() => {
    const groups: {
      section: "general" | "assigned_to" | "created_by" | "tags";
      label: string;
      filters: { filter: FilterDescriptor; index: number }[];
    }[] = [
      { section: "general", label: t.filterBy || "Filter By", filters: [] },
      { section: "assigned_to", label: t.assignedTo || "Assigned To", filters: [] },
      { section: "created_by", label: t.createdBy || "Created By", filters: [] },
      { section: "tags", label: t.tags || "Tags", filters: [] },
    ];

    filters.forEach((filter, index) => {
      const section = filter._metadata?.section || "general";
      const group = groups.find((g) => g.section === section);
      if (group) {
        group.filters.push({ filter, index });
      }
    });

    return groups.filter((g) => g.filters.length > 0);
  }, [filters, t]);

  const formatFilterValue = (filter: FilterDescriptor): string => {
    // Handle _assigned_to field (new format - plain email or array)
    if (filter.field === "_assigned_to") {
      if (Array.isArray(filter.value)) {
        return filter.value.join(", ");
      }
      return String(filter.value);
    }
    // Legacy _assign field support (for backward compatibility during migration)
    if (filter.field === "_assign") {
      // Extract user email from: %\"user@example.com\"%
      const match = String(filter.value).match(/%\\?"([^"]+)\\"?%/);
      return match ? match[1] : String(filter.value);
    }
    if (filter.field === "_user_tags") {
      // Extract tag from: %tagname%
      const match = String(filter.value).match(/%(.+?)%/);
      return match ? match[1] : String(filter.value);
    }
    if (Array.isArray(filter.value)) {
      return filter.value.join(", ");
    }
    return String(filter.value || "");
  };

  const getFieldLabel = (filter: FilterDescriptor): string => {
    // For special fields, use translated labels
    if (filter.field === "_assigned_to" || filter.field === "_assign") {
      return t.assignedTo || "Assigned To";
    }
    if (filter.field === "owner") {
      return t.createdBy || "Created By";
    }
    if (filter.field === "_user_tags") {
      return t.tags || "Tags";
    }
    // For general filters, show field name
    return filter.field;
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2">
      {groupedFilters.map((group) => (
        <div key={group.section} className="flex flex-wrap items-center gap-2">
          {group.filters.map(({ filter, index }) => (
            <Badge
              key={index}
              variant="outline"
              className="flex items-center gap-1 px-2 py-1 text-xs bg-white dark:bg-gray-900 border-border/60 hover:bg-muted/50"
            >
              <span className="font-medium">{getFieldLabel(filter)}</span>
              <span className="text-muted-foreground">:</span>
              <span>{formatFilterValue(filter)}</span>
              {filter._metadata?.logicMode && (
                <span className="text-muted-foreground">
                  ({filter._metadata.logicMode.toUpperCase()})
                </span>
              )}
              <button
                onClick={() => onRemoveFilter(index)}
                className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                aria-label={t.removeFilter || "Remove filter"}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ))}
      {filters.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="h-7 text-xs"
        >
          {t.clearAll || "Clear All"}
        </Button>
      )}
    </div>
  );
}

